import { Scene, _ } from "~/route/globals";
import { node, icon, text } from "~/util/dom";
import { ROUTE_CONST } from "~/route/const";
import WasmStoreGeometry from "~/wasm/store/wasm-store-geometry";
import { convertSmapStyleToWasmGeometryStyleHash } from "~/wasm/utils";
import WasmStoreMarker from "~/wasm/store/wasm-store-marker";
import { WASM_ZINDEX } from "~/wasm/const";
import { TWasmTextureNames } from "~/wasm/textures-data";
import { generatePopupAngles } from "~/util/wasm";

export function createGeometry(conf) {
	const state = conf.state;
	const items = state.route.items;
	const closureMarkers = [];
	const activeIndex = state.route.index;
	const pointsCoords = [];
	const map = conf.map;
	const geometry = conf.geometry || [];
	const trafficGeom = conf.trafficGeom || [];
	const closuresGeom = conf.closuresGeom || [];
	const points = conf.points || [];
	const wasmGeometries: Array<Array<WasmStoreGeometry>> = [];
	const wasmTrafficGeometries: Array<{ geoms: Array<WasmStoreGeometry>; }> = [];
	const wasmPointsMarkers: Array<WasmStoreMarker> = [];

	const addPoint = (coords, style, index, add) => {
		if (!pointsCoords[index]) {
			pointsCoords[index] = [];
		}

		const inArray = pointsCoords[index].filter(point => {
			if (point.coords.equals(coords)) {
				return true;
			}

			return false;
		});

		if (inArray.length) {
			pointsCoords[index].splice(pointsCoords[index].indexOf(inArray[0]), 1);
		}

		pointsCoords[index].push({
			coords,
			style,
			add,
		});
	};

	const createClosureMarker = (closure, add) => {
		const elm = node("span", {});
		let type = state.route.params.type;

		if (state.route.items[0].planTrip) {
			const keys = Object.keys(ROUTE_CONST.types);
			const tripCriterion = state.route.params.tripCriterion;

			keys.forEach(key => {
				const typ = ROUTE_CONST.types[key];

				if (typ.criterion.indexOf(tripCriterion) > -1) {
					type = typ.type;
				}
			});
		}

		function getClosureSvgIcons() {
			let svg = "";
			let wasmIcon: TWasmTextureNames = "wasm-no-entry";

			if (closure.type === "no_winter_maintenance") {
				svg = "warning-sign";
				wasmIcon = "wasm-warning-sign";
			} else if (closure.type === "via_ferrata") {
				svg = "warning-ferrata";
				wasmIcon = "wasm-warning-ferrata";
			} else if (closure.source && closure.id) {
				svg = "no-entry";
				wasmIcon = "wasm-no-entry";
			} else {
				svg = ROUTE_CONST.closureImages[type];

				switch (svg) {
					case "no-entry-car":
						wasmIcon = "wasm-no-entry-car";
						break;

					case "no-entry-bike":
						wasmIcon = "wasm-no-entry-bike";
						break;

					case "no-entry-walk":
						wasmIcon = "wasm-no-entry-walk";
						break;

					default:
				}
			}

			return {
				svg,
				wasmIcon,
			};
		}

		const { svg, wasmIcon } = getClosureSvgIcons();

		elm.appendChild(icon(svg));
		elm.appendChild(text(closure.name));
		const coords = closure.popup && closure.popup.coords !== undefined ? Scene.coordsFromWGS84(closure.popup.mark[0], closure.popup.mark[1]) : closure.geom[0];
		const angle = closure.popup && closure.popup.azimuth !== undefined ? Math.round(closure.popup.azimuth) : 135;
		const bubble = Scene.createMarkerBubble(coords, null, {
			elm,
			className: "route-closure",
			arrowLength: 24,
			arrowCenter: [12, 12],
			bubbleHeight: 24,
			angle,
		});

		if (add) {
			map.closureLayer.addMarker(bubble);
		}

		let poiSource = "";
		let poiId = 0;

		if (closure.source && closure.id) {
			poiSource = closure.source;
			poiId = closure.id;
		} else if (closure.geom) {
			poiSource = "closure";
			poiId = coords.toWGS84().join(",");
		}

		const wasmMarker = new WasmStoreMarker({
			type: "route-popup-closure",
			lon: coords.x,
			lat: coords.y,
			visible: add,
			interactionType: "click-hover",
			customData: {
				popupArrowAngles: generatePopupAngles((angle + 180) % 360),
				routePopupClosure: {
					icon: wasmIcon,
					text: closure.name,
				},
				poi: {
					source: poiSource,
					id: poiId,
					title: closure.name,
					description: closure.command,
				},
			},
			zIndex: WASM_ZINDEX.marker.routePopupClosure,
		});

		return {
			closureMarker: bubble,
			wasmMarker,
		};
	};

	map.geomLayer.removeAll();
	map.activeGeomLayer.removeAll();
	map.hoverGeomLayer.removeAll();
	map.pointLayer.removeAll();
	map.closureLayer.removeAll();
	map.interactive.clear();

	while (geometry.length) {
		const geom = geometry.pop();

		while (geom.length) {
			geom.pop().destroy();
		}
	}

	while (trafficGeom.length) {
		const geom = trafficGeom.pop();

		while (geom.length) {
			geom.pop().destroy();
		}
	}

	while (points.length) {
		const index = points.pop();

		while (index.length) {
			index.pop().destroy();
		}
	}

	while (closuresGeom.length) {
		const index = closuresGeom.pop();

		while (index.length) {
			index.pop().destroy();
		}
	}

	if (!state.route.responseData || !state.route.responseData.length) {
		return { geometry, trafficGeom, closuresGeom, points, closureMarkers, wasmGeometries, wasmPointsMarkers, wasmTrafficGeometries };
	}

	if (items.length) {
		let style = ROUTE_CONST.DEFAULT_LINE_STYLE;

		if (items.length > 1 && state.route.params.type === ROUTE_CONST.TYPE_CAR) {
			style = {
				...style,
				...ROUTE_CONST.CAR_LINE_STYLE_MODIFY,
				...state.route.trafficLoaded ? ROUTE_CONST.CAR_TRAFFIC_LINE_STYLE_MODIFY : {},
			};
		}

		// pubt
		if (state.route.params.type === ROUTE_CONST.TYPE_PUBT && items.length > 1 && state.route.params.criterion === ROUTE_CONST.types.pubt.criterion[0]) {
			geometry[0] = [];
			wasmGeometries[0] = [];
			for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
				const item = items[itemIndex];
				const pubtData = item.pubtData && item.pubtData[0] ? JSON.parse(item.pubtData[0]) : {};
				let itemCoords = [];

				if (pubtData && pubtData.items && pubtData.items.length) {
					for (let index = 0; index < pubtData.items.length; index++) {
						const pubtDataItem = pubtData.items[index];

						style = ROUTE_CONST.DEFAULT_LINE_STYLE;
						if (pubtDataItem.geometry) {
							// pesky a jinak mimo definovane - sediva
							if (pubtDataItem.type < 2 && !pubtDataItem.routeName) {
								style = { ...style, ...ROUTE_CONST.PUBT_WALK_LINE_STYLE_MODIFY };
							} else {
								style = { ...style, ...ROUTE_CONST.PUBT_LINE_STYLE_MODIFY };
							}

							const geomCoords = Scene.stringToCoords(pubtDataItem.geometry);
							const prev = pubtData.items[index-1];
							const next = pubtData.items[index+1];

							if (pubtDataItem.type === 1 && next && next.type !== 1 && next.geometry) {
								const coor = Scene.stringToCoords(next.geometry);

								geomCoords.push(coor[0]);
							}

							if (pubtDataItem.type === 1 && prev && prev.type !== 1 && prev.geometry) {
								const coor = Scene.stringToCoords(prev.geometry);

								geomCoords.unshift(coor[coor.length-1]);
							}

							const geom = Scene.createGeometrySmart(Scene.GEOMETRY.POLYLINE, null, geomCoords, style);

							//console.log(["#1", style]);
							wasmGeometries[0].push(new WasmStoreGeometry({
								type: "custom",
								strPoints: [Scene.coordsToString(geomCoords)],
								customStyleHash: {
									// @ts-ignore
									standard: convertSmapStyleToWasmGeometryStyleHash(style, true),
									// @ts-ignore
									inactive: convertSmapStyleToWasmGeometryStyleHash({
										...style,
										color: ROUTE_CONST.DEACTIVE_LINE_COLOR.color,
									}, true),
								},
								interactionType: "click-hover",
								customData: {
									order: itemIndex + 1,
									route: {
										routeIndex: 0,
									},
								},
								zIndex: WASM_ZINDEX.geometry.routeRoutes,
							}));
							geometry[0].push(geom);
							map.geomLayer.addSmartGeometry(geom);
							itemCoords = itemCoords.concat(geomCoords);
						}
					}

					map.interactive.addCoords(0, itemCoords);
				}
			}
		} else {
			const closures = [];
			const warnings = [];
			const pathLength = [];
			let last = null;
			const trafficGeomC = [];
			const trafficGeomS = [];
			const wasmTraffic: Array<{[key: number]: { styleActive: any; styleInactive: any; coords: Array<Array<string>>; }}> = [];

			// geometrie
			for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
				const item = items[itemIndex];

				pathLength[item] = [];
				if (item.path && item.path.length && item.path[activeIndex]) {
					last = item;

					for (let index = 0; index < item.path.length; index++) {
						const geomCoords = Scene.stringToCoords(item.path[index]);
						let withoutClosure = [];
						let closureExist = false;

						if (!geometry[index]) {
							geometry[index] = [];
							trafficGeom[index] = [];
							trafficGeomC[index] = [];
							trafficGeomS[index] = [];
						}

						if (!wasmGeometries[index]) {
							wasmGeometries[index] = [];
						}

						wasmTraffic[index] = {};
						addPoint(Scene.stringToCoords(item.path[activeIndex])[0], { ...ROUTE_CONST.DEFAULT_CIRCLE_STYLE }, index, index === activeIndex);

						// odebrani indexu pro uzavirku - ale jen pro vykresleni, pro pocitani bereme celou geom
						if (item.closures && item.closures.length && item.closures[index]) {
							let prev = null;

							for (let num = 0; num < item.closures[index].length; num++) {
								const cl = item.closures[index][num];
								const start = cl.start;
								const end = cl.end;
								const next = item.closures[index][num+1];

								if ((!prev && start !== 0) || (prev && prev.end !== start)) {
									withoutClosure.push(geomCoords.slice(prev ? prev.end : 0, start + 1));
								}

								if (!next && end !== geomCoords.length-1) {
									withoutClosure.push(geomCoords.slice(end, geomCoords.length));
								}

								prev = cl;
								closureExist = true;
							}
						}

						if (!closureExist) {
							withoutClosure = [geomCoords.slice()];
						}

						for (let num = 0; num < withoutClosure.length; num++) {
							const wc = withoutClosure[num];
							const geom = Scene.createGeometrySmart(Scene.GEOMETRY.POLYLINE, null, wc, style);

							//console.log(["#2", style]);
							wasmGeometries[index].push(new WasmStoreGeometry({
								type: "custom",
								strPoints: [Scene.coordsToString(wc)],
								customStyleHash: {
									// @ts-ignore
									standard: convertSmapStyleToWasmGeometryStyleHash(style, true),
									// @ts-ignore
									inactive: convertSmapStyleToWasmGeometryStyleHash({
										...style,
										color: ROUTE_CONST.DEACTIVE_LINE_COLOR.color,
									}, true),
								},
								interactionType: "click-hover",
								customData: {
									order: itemIndex + 1,
									route: {
										routeIndex: index,
									},
								},
								zIndex: WASM_ZINDEX.geometry.routeRoutes,
							}));
							geometry[index].push(geom);
							map.geomLayer.addSmartGeometry(geom);
						}

						map.interactive.addCoords(index, geomCoords);

						if (!pathLength[item][index]) {
							pathLength[item][index] = [];
						}
						pathLength[item][index].push(geomCoords.length);

						// mame nejaka data k dopravni situaci a zaroven mame id casti trasy
						if (item.route_part_ids.length && state.route.params.type === ROUTE_CONST.TYPE_CAR && state.route.trafficLoaded) {
							// data o doprave k aktualnimu itemu
							const traffic = state.route.traffic[item.route_part_ids[index]];

							if (traffic && traffic.length) {
								for (let trafficIndex = 0; trafficIndex < traffic.length; trafficIndex++) {
									const intensity = traffic[trafficIndex].intensity;

									if (intensity < 2) {
										continue;
									}

									const start = traffic[trafficIndex].position;
									const end = trafficIndex < traffic.length - 1 ? traffic[trafficIndex + 1].position : geomCoords.length;
									const trafficStyle = {
										trafficType: intensity,
										...ROUTE_CONST.DEFAULT_LINE_STYLE,
										...ROUTE_CONST.CAR_LINE_STYLE_MODIFY,
										...ROUTE_CONST.TRAFFIC_LINE_STYLE,
										...ROUTE_CONST.TRAFFIC_LINE_COLOR[intensity].active,
									};

									trafficGeomC[index].push(geomCoords.slice(start, end + 1));
									trafficGeomS[index].push(trafficStyle);

									// wasm
									if (!(intensity in wasmTraffic[index])) {
										wasmTraffic[index][intensity] = {
											styleActive: trafficStyle,
											styleInactive: {
												...trafficStyle,
												...ROUTE_CONST.TRAFFIC_LINE_COLOR[intensity].deactive,
											},
											coords: [],
										};
									}

									wasmTraffic[index][intensity].coords.push([Scene.coordsToString(geomCoords.slice(start, end + 1))]);
								}
							}
						}
					}
				}
			}

			for (let index = 0; index < trafficGeomC.length; index++) {
				if (trafficGeomC[index].length) {
					const geom = Scene.createGeometry(Scene.GEOMETRY.TRAFFICPATH, null, trafficGeomC[index], trafficGeomS[index]);

					trafficGeom[index].push(geom);
					map.geomLayer.addGeometry(geom);
				}
			}

			for (const wasmTrafficItem of wasmTraffic) {
				if (Object.keys(wasmTrafficItem).length > 0) {
					// zkusime nastrelit wasm geometrie trafficu
					const allVariants = Object.keys(wasmTrafficItem);
					const newItem = {
						geoms: [],
					};

					for (const variant of allVariants) {
						const source = wasmTrafficItem[variant];

						newItem.geoms.push(new WasmStoreGeometry({
							type: "custom",
							strPoints: source.coords,
							customStyleHash: {
								standard: convertSmapStyleToWasmGeometryStyleHash({
									...source.styleActive,
									// nechceme obrys + ctvercove konce
									outlineWidth: 0,
									endCap: 2,
									// 6 byla dost macata, zkusime 5 nebo 4
									width: 5,
								}, true),
								inactive: convertSmapStyleToWasmGeometryStyleHash({
									...source.styleInactive,
									// nechceme obrys + ctvercove konce
									outlineWidth: 0,
									endCap: 2,
									width: 5,
								}, true),
							},
							// pro traffic nechceme interactionType
							zIndex: WASM_ZINDEX.geometry.routeTraffic,
						}));
					}

					wasmTrafficGeometries.push(newItem);
				}
			}

			if (last) {
				for (let index = 0; index < last.path.length; index++) {
					const coords = Scene.stringToCoords(last.path[index]);

					addPoint(coords[coords.length-1], { ...ROUTE_CONST.DEFAULT_CIRCLE_STYLE }, index, index === activeIndex);
				}
			}

			// napocitani uzavirek a jejich zakresleni do mapy
			let prev = null;

			for (let num = 0; num < items.length; num++) {
				const item = items[num];

				if (item.closures && item.closures.length) {
					for (let index = 0; index < item.closures.length; index++) {
						const cl = item.closures[index];

						if (!closures[index]) {
							closures[index] = [];
						}
						if (cl) {
							for (let indexCl = 0; indexCl < cl.length; indexCl++) {
								const closure = cl[indexCl];
								const nc = { ...closure };

								if (item.path[index]) {
									nc.geom = Scene.stringToCoords(item.path[index]);
									nc.geomLength = nc.geom.length-1;

									if (closure.end === nc.geomLength) {
										addPoint(nc.geom[nc.geomLength], { ...ROUTE_CONST.DEFAULT_CIRCLE_STYLE, ...ROUTE_CONST.CLOSURE_CIRCLE_STYLE }, index, activeIndex === index);
									}

									nc.geom = nc.geom.slice(closure.start, closure.end + 1);

									if (closures[index].length && closure.start === 0) {
										if (prev && prev.end === prev.geomLength && ((prev.id && prev.id === closure.id) || !prev.id)) {
											prev.geomLength += nc.geomLength;
											prev.end += nc.end;
											prev.geom = prev.geom.concat(nc.geom);
										} else {
											closures[index].push(nc);
											prev = nc;
										}
									} else {
										closures[index].push(nc);
										prev = nc;
									}
								} else {
									prev = nc;
								}
							}
						}
					}
				} else {
					prev = null;
				}
			}

			// to same, ale pro varovani, ktera nemaji geometrii
			prev = null;

			for (let num = 0; num < items.length; num++) {
				const item = items[num];

				if (item.warnings?.length) {
					for (let index = 0; index < item.warnings.length; index++) {
						const cl = item.warnings[index];

						if (!warnings[index]) {
							warnings[index] = [];
						}
						if (cl) {
							for (let indexWarning = 0; indexWarning < cl.length; indexWarning++) {
								const warning = cl[indexWarning];
								const nc = { ...warning };

								if (item.path[index]) {
									nc.geom = Scene.stringToCoords(item.path[index]);
									nc.geomLength = nc.geom.length - 1;

									if (warning.end === nc.geomLength) {
										addPoint(nc.geom[nc.geomLength], { ...ROUTE_CONST.DEFAULT_CIRCLE_STYLE, ...ROUTE_CONST.CLOSURE_CIRCLE_STYLE }, index, activeIndex === index);
									}

									nc.geom = nc.geom.slice(warning.start, warning.end + 1);

									if (warnings[index].length && warning.start === 0) {
										if (prev && prev.end === prev.geomLength && ((prev.id && prev.id === warning.id) || !prev.id)) {
											prev.geomLength += nc.geomLength;
											prev.end += nc.end;
											prev.geom = prev.geom.concat(nc.geom);
										} else {
											warnings[index].push(nc);
											prev = nc;
										}
									} else {
										warnings[index].push(nc);
										prev = nc;
									}
								} else {
									prev = nc;
								}
							}
						}
					}
				} else {
					prev = null;
				}
			}

			style = { ...ROUTE_CONST.DEFAULT_LINE_STYLE, ...ROUTE_CONST.CLOSURE_LINE_COLOR };

			for (let index = 0; index < closures.length; index++) {
				const cls = closures[index];

				if (!closuresGeom[index]) {
					closuresGeom[index] = [];
				}
				if (!closureMarkers[index]) {
					closureMarkers[index] = [];
				}

				const crds = map.interactive.allCoords[index];

				if (crds) {
					for (let clsIndex = 0; clsIndex < cls.length; clsIndex++) {
						const cl = cls[clsIndex];
						const geom = Scene.createGeometrySmart(Scene.GEOMETRY.POLYLINE, null, cl.geom, style);

						//console.log(["#4", style]);
						wasmGeometries[index].push(new WasmStoreGeometry({
							type: "custom",
							strPoints: [Scene.coordsToString(cl.geom)],
							customStyleHash: {
								// @ts-ignore
								standard: convertSmapStyleToWasmGeometryStyleHash(style, true),
							},
							interactionType: "click-hover",
							zIndex: WASM_ZINDEX.geometry.routeRoutes,
							// TODO: zjistit spravne, k jakemu itemu se tahle geometrie vaze
							customData: {
								order: clsIndex + 1,
								route: {
									routeIndex: index,
								},
							},
						}));
						map.geomLayer.addSmartGeometry(geom);
						closuresGeom[index].push(geom);

						addPoint(cl.geom[0], { ...ROUTE_CONST.DEFAULT_CIRCLE_STYLE, ...ROUTE_CONST.CLOSURE_CIRCLE_STYLE }, index, index === activeIndex);
						addPoint(cl.geom[cl.geom.length-1], { ...ROUTE_CONST.DEFAULT_CIRCLE_STYLE, ...ROUTE_CONST.CLOSURE_CIRCLE_STYLE }, index, index === activeIndex);

						const { closureMarker, wasmMarker } = createClosureMarker(cl, activeIndex === index);

						closureMarkers[index].push({
							marker: closureMarker,
							wasmMarker,
							source: cl.source,
							id: cl.id,
							title: cl.name,
							description: cl.command,
							geom: cl.geom,
						});
					}
				}
			}

			// pro warningy vyrobime bubliny tam, co pro closures, ale ne geometrie
			for (let index = 0; index < warnings.length; index++) {
				const cls = warnings[index];

				if (!closureMarkers[index]) {
					closureMarkers[index] = [];
				}

				const crds = map.interactive.allCoords[index];

				if (crds) {
					for (let closureIndex = 0; closureIndex < cls.length; closureIndex++) {
						const cl = cls[closureIndex];

						addPoint(cl.geom[0], { ...ROUTE_CONST.DEFAULT_CIRCLE_STYLE, ...ROUTE_CONST.CLOSURE_CIRCLE_STYLE }, index, index === activeIndex);

						const { closureMarker, wasmMarker } = createClosureMarker(cl, activeIndex === index);

						closureMarkers[index].push({
							marker: closureMarker,
							wasmMarker,
							source: cl.source,
							id: cl.id,
							title: cl.name,
							description: cl.type === "via_ferrata" ? _("route.warning.ferrata.description") : _("route.warning.description"),
							geom: cl.geom,
						});
					}
				}
			}

			for (let index = 0; index < pointsCoords.length; index++) {
				const pts = pointsCoords[index];

				if (!points[index]) {
					points[index] = [];
				}

				for (let num = 0; num < pts.length; num++) {
					const pt = pts[num];
					const point = Scene.createGeometry(Scene.GEOMETRY.CIRCLE, null, [pt.coords, ROUTE_CONST.DEFAULT_CIRCLE_RADIUS], pt.style);

					if (pt.add) {
						map.pointLayer.addGeometry(point);
					}

					points[index].push(point);
					// bod pro wasm
					wasmPointsMarkers.push(new WasmStoreMarker({
						type: "route-point-circle",
						lon: pt.coords.x,
						lat: pt.coords.y,
						zIndex: WASM_ZINDEX.marker.routePointCircle,
						customData: {
							routePoint: {
								color: pt.style.outlineColor,
								radius: ROUTE_CONST.DEFAULT_CIRCLE_RADIUS,
							},
						},
					}));
				}
			}
		}
	}

	return {
		geometry,
		trafficGeom,
		closuresGeom,
		points,
		closureMarkers,
		wasmGeometries,
		wasmPointsMarkers,
		wasmTrafficGeometries,
	};
}
